import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ParticipantCohortListItem } from '../services/api/participantPortal.js';

interface ParticipantCohortState {
  selectedCohortId: string | null;
  cohorts: ParticipantCohortListItem[];
}

interface ParticipantCohortActions {
  setSelectedCohortId: (cohortId: string | null) => void;
  setCohorts: (cohorts: ParticipantCohortListItem[]) => void;
}

type ParticipantCohortStore = ParticipantCohortState & ParticipantCohortActions;

export const useParticipantCohortStore = create<ParticipantCohortStore>()(
  persist(
    (set) => ({
      selectedCohortId: null,
      cohorts: [],

      setSelectedCohortId: (cohortId: string | null) => {
        set({ selectedCohortId: cohortId });
      },

      setCohorts: (cohorts: ParticipantCohortListItem[]) => {
        set({ cohorts });
      },
    }),
    {
      name: 'participant-cohort',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedCohortId: state.selectedCohortId,
      }),
    },
  ),
);

/**
 * MED-778 + MED-1307 (MDF-184): app access state of the selected cohort.
 * - 'features_disabled': Participant Features (master toggle) is off.
 * - 'app_access_disabled': Participant Features is on, app access is off.
 * - 'enabled': otherwise, and when no cohort is selected (safe default).
 * A missing field counts as enabled, so an older API response does not
 * lock anyone out.
 */
export type SelectedCohortAccess = 'enabled' | 'features_disabled' | 'app_access_disabled';

export function useSelectedCohortAccess(): SelectedCohortAccess {
  const { selectedCohortId, cohorts } = useParticipantCohortStore();
  if (!selectedCohortId || cohorts.length === 0) return 'enabled';
  const selected = cohorts.find((c) => c.id === selectedCohortId);
  if (!selected) return 'enabled';
  if (selected.participant_features_enabled === false) return 'features_disabled';
  if (selected.app_access_enabled === false) return 'app_access_disabled';
  return 'enabled';
}
